function num(v) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtNum(v, d = 2) {
  const n = num(v);
  if (n == null) return '';
  return n.toFixed(d);
}

function fmtUsd(v) {
  const n = num(v);
  if (n == null) return '';
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function fmtTs(v) {
  if (!v) return '';
  const d = typeof v === 'number' ? new Date(v) : new Date(String(v).includes('T') ? v : `${String(v).replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-US', { timeZone: 'America/Chicago', hour12: false });
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

function fileBase(report) {
  return `omni-disc-scalping-${stamp()}-${report.paperMode ? 'paper' : 'live'}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2500);
}

function csvCell(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells) {
  return cells.map(csvCell).join(',');
}

export function buildDiscScalpingCsv(report) {
  const s = report.summary || {};
  const lines = [
    csvRow(['Omni Scalping', 'Discretionary Scalping Trade & PnL Report']),
    csvRow(['Generated', fmtTs(report.generatedAt), 'Timezone', 'America/Chicago']),
    csvRow(['Mode', report.paperMode ? 'Paper' : 'Live']),
    csvRow(['Account', report.email || '']),
    csvRow(['Wallet', report.walletAddress || '']),
    csvRow(['Equity USD', fmtNum(s.equityUsd)]),
    csvRow(['Realized PnL USD', fmtNum(s.realizedPnlUsd)]),
    csvRow(['Unrealized PnL USD', fmtNum(s.unrealizedPnlUsd)]),
    csvRow(['Total PnL USD', fmtNum(s.totalPnlUsd)]),
    csvRow(['Fees USD', fmtNum(s.feesUsd)]),
    csvRow(['Commission owed USD', fmtNum(s.commissionOwedUsd)]),
    csvRow(['Open positions', s.openPositions, 'Fills', s.fills, 'Orders', s.orders]),
    '',
    'OPEN POSITIONS',
    csvRow(['Market', 'Dex', 'Side', 'Size', 'Entry', 'Mark', 'Value USD', 'Unrealized PnL', 'ROE', 'Leverage', 'Margin USD']),
    ...(report.positions || []).map((p) =>
      csvRow([
        p.symbol,
        p.dex,
        p.side,
        fmtNum(p.sizeContracts, 6),
        fmtNum(p.entryPrice, 6),
        fmtNum(p.markPrice, 6),
        fmtNum(p.positionValue),
        fmtNum(p.unrealizedPnl),
        p.returnOnEquity == null ? '' : fmtNum(p.returnOnEquity * 100, 2),
        fmtNum(p.leverage, 0),
        fmtNum(p.marginUsed),
      ])
    ),
    '',
    'TRADES',
    csvRow([
      'Time',
      'Market',
      'Dex',
      'Side',
      'Type',
      'Reduce only',
      'Size',
      'Limit',
      'Fill',
      'Notional USD',
      'Realized PnL USD',
      'HL fee USD',
      'Omni commission USD',
      'Status',
      'Paper',
      'Order id',
      'Error',
    ]),
    ...(report.trades || []).map((t) =>
      csvRow([
        fmtTs(t.time),
        t.symbol,
        t.dex,
        t.side,
        t.orderType,
        t.reduceOnly ? 'yes' : '',
        fmtNum(t.sizeContracts, 6),
        fmtNum(t.limitPrice, 6),
        fmtNum(t.fillPrice, 6),
        fmtNum(t.notionalUsd),
        fmtNum(t.realizedPnlUsd),
        fmtNum(t.hlFeeUsd),
        fmtNum(t.commissionUsd),
        t.status,
        t.isPaper ? 'yes' : '',
        t.id,
        t.error || '',
      ])
    ),
  ];
  return `${lines.join('\r\n')}\r\n`;
}

/* ---------------- PDF (Helvetica, multi-page letter, fixed columns) ---------------- */

function pdfEscape(s) {
  return String(s ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

/** Helvetica (WinAnsi) widths in 1000-unit em. Missing glyphs use 600. */
const HELV_W = {
  ' ': 278, '!': 278, '"': 355, '#': 556, $: 556, '%': 889, '&': 667, "'": 191,
  '(': 333, ')': 333, '*': 389, '+': 584, ',': 278, '-': 333, '.': 278, '/': 278,
  0: 556, 1: 556, 2: 556, 3: 556, 4: 556, 5: 556, 6: 556, 7: 556, 8: 556, 9: 556,
  ':': 278, ';': 278, '<': 584, '=': 584, '>': 584, '?': 556, '@': 1015,
  A: 667, B: 667, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278, J: 500,
  K: 667, L: 556, M: 833, N: 722, O: 778, P: 667, Q: 778, R: 722, S: 667, T: 611,
  U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
  '[': 278, '\\': 278, ']': 278, '^': 469, _: 556, '`': 222,
  a: 556, b: 556, c: 500, d: 556, e: 556, f: 278, g: 556, h: 556, i: 222, j: 222,
  k: 500, l: 222, m: 833, n: 556, o: 556, p: 556, q: 556, r: 333, s: 500, t: 278,
  u: 556, v: 500, w: 722, x: 500, y: 500, z: 500,
};
const HELV_BOLD_EXTRA = 1.04;

function textWidth(str, size, bold = false) {
  let w = 0;
  for (const ch of String(str ?? '')) w += HELV_W[ch] || 600;
  return ((w * size) / 1000) * (bold ? HELV_BOLD_EXTRA : 1);
}

function fitText(str, size, maxW, bold = false) {
  let t = String(str ?? '');
  if (textWidth(t, size, bold) <= maxW) return t;
  while (t.length && textWidth(`${t}…`, size, bold) > maxW) t = t.slice(0, -1);
  return t ? `${t}…` : '';
}

export function buildPdfBytes(report) {
  const W = 612;
  const H = 792;
  const M = 36;
  const pages = [];
  let y = 0;
  let stream = [];

  const navy = '0.067 0.118 0.227';
  const muted = '0.40 0.45 0.52';
  const ink = '0.07 0.09 0.13';
  const line = '0.82 0.85 0.90';
  const headBg = '0.93 0.95 0.97';
  const zebra = '0.97 0.98 0.99';
  const green = '0.09 0.55 0.29';
  const red = '0.73 0.15 0.15';
  const white = '1 1 1';
  const ROW_H = 16;
  const PAD = 4;

  function newPage() {
    stream = [];
    pages.push(stream);
    y = H - M;
  }

  function ensure(h) {
    if (y - h < M + 24) newPage();
  }

  function rect(x, yy, w, h, rgb) {
    stream.push(`${rgb} rg ${x.toFixed(2)} ${yy.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f`);
  }

  /** Color outside BT; Td from identity so every string is absolutely placed. */
  function text(x, yy, str, size, rgb = ink, bold = false) {
    const s = String(str ?? '');
    if (!s) return;
    stream.push(`${rgb} rg`);
    stream.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${x.toFixed(2)} ${yy.toFixed(2)} Td (${pdfEscape(s)}) Tj ET`);
  }

  function cell(x, yy, w, str, size, rgb, align, bold) {
    const label = fitText(str, size, w - PAD * 2, bold);
    if (!label) return;
    const tw = textWidth(label, size, bold);
    const tx = align === 'r' ? x + w - PAD - tw : x + PAD;
    text(tx, yy, label, size, rgb, bold);
  }

  function hline(x, yy, w) {
    stream.push(`${line} RG 0.4 w ${x.toFixed(2)} ${yy.toFixed(2)} m ${(x + w).toFixed(2)} ${yy.toFixed(2)} l S`);
  }

  newPage();
  rect(0, H - 70, W, 70, navy);
  text(M, H - 32, 'OMNI SCALPING', 11, white, true);
  text(M, H - 50, 'Discretionary Scalping   |   Trade & PnL Report', 13, white, true);
  text(W - M - 50, H - 32, report.paperMode ? 'PAPER' : 'LIVE', 11, '0.70 0.86 0.98', true);
  y = H - 88;

  const s = report.summary || {};
  text(M, y, `Generated   ${fmtTs(report.generatedAt)} CT`, 8, muted);
  y -= 13;
  text(M, y, `Account   ${report.email || '—'}`, 8, muted);
  y -= 13;
  text(M, y, `Wallet   ${report.walletAddress || '—'}`, 8, muted);
  y -= 22;

  const kpis = [
    ['Equity', fmtUsd(s.equityUsd), false],
    ['Realized PnL', fmtUsd(s.realizedPnlUsd), true],
    ['Unrealized PnL', fmtUsd(s.unrealizedPnlUsd), true],
    ['Total PnL', fmtUsd(s.totalPnlUsd), true],
    ['Fees', fmtUsd(s.feesUsd), false],
    ['Fills', String(s.fills ?? 0), false],
  ];
  const gap = 8;
  const boxW = (W - M * 2 - gap * 5) / 6;
  const boxH = 42;
  kpis.forEach((k, i) => {
    const x = M + i * (boxW + gap);
    const bottom = y - boxH;
    rect(x, bottom, boxW, boxH, headBg);
    const n = num(String(k[1]).replace(/[$,]/g, ''));
    const rgb = k[2] && n != null ? (n >= 0 ? green : red) : ink;
    cell(x, bottom + 26, boxW, k[0], 7, muted, 'l', true);
    cell(x, bottom + 10, boxW, k[1] || '—', 9, rgb, 'l', true);
  });
  y -= boxH + 18;

  function table(title, headers, rows, widths, aligns) {
    const tableW = widths.reduce((a, b) => a + b, 0);
    const colX = [];
    widths.reduce((acc, w) => {
      colX.push(acc);
      return acc + w;
    }, M);

    const drawHead = () => {
      ensure(ROW_H + 8);
      rect(M, y - 4, tableW, ROW_H, headBg);
      headers.forEach((h, i) => {
        cell(colX[i], y, widths[i], h, 7, muted, aligns[i], true);
      });
      y -= ROW_H;
    };

    ensure(40);
    text(M, y, title, 11, navy, true);
    y -= 6;
    hline(M, y, tableW);
    y -= 14;
    drawHead();

    if (!rows.length) {
      text(M + PAD, y, 'None', 8, muted);
      y -= 18;
      return;
    }

    rows.forEach((row, ri) => {
      if (y < M + 40) {
        newPage();
        y = H - M;
        text(M, y, `${title}  (continued)`, 10, navy, true);
        y -= 16;
        drawHead();
      }
      if (ri % 2 === 1) rect(M, y - 4, tableW, ROW_H, zebra);
      row.forEach((c, i) => {
        const raw = c == null ? '' : String(c);
        const n = num(String(raw).replace(/[$,%]/g, ''));
        let rgb = ink;
        const head = String(headers[i] || '').toLowerCase();
        if (head.includes('pnl') && n != null) rgb = n >= 0 ? green : red;
        if (head === 'side') rgb = /sell|short/i.test(raw) ? red : /buy|long/i.test(raw) ? green : ink;
        cell(colX[i], y, widths[i], raw, 7.5, rgb, aligns[i], false);
      });
      y -= ROW_H;
    });
    y -= 16;
  }

  table(
    'Open positions',
    ['Market', 'Side', 'Size', 'Entry', 'Mark', 'Value', 'uPnL', 'ROE'],
    (report.positions || []).map((p) => [
      `${p.symbol}${p.dex && p.dex !== 'main' ? ` ${p.dex}` : ''}`,
      p.side,
      fmtNum(p.sizeContracts, 5),
      fmtNum(p.entryPrice, 2),
      fmtNum(p.markPrice, 2),
      fmtUsd(p.positionValue),
      fmtUsd(p.unrealizedPnl),
      p.returnOnEquity == null ? '' : `${fmtNum(p.returnOnEquity * 100, 1)}%`,
    ]),
    [62, 48, 62, 70, 70, 76, 76, 50],
    ['l', 'l', 'r', 'r', 'r', 'r', 'r', 'r']
  );

  table(
    'Trade blotter',
    ['Time', 'Mkt', 'Side', 'Type', 'Size', 'Fill', 'Notional', 'rPnL', 'Status'],
    (report.trades || []).map((t) => [
      fmtTs(t.time),
      t.symbol,
      `${t.side}${t.reduceOnly ? ' ro' : ''}`,
      t.orderType,
      fmtNum(t.sizeContracts, 5),
      fmtNum(t.fillPrice, 2),
      fmtUsd(t.notionalUsd),
      t.realizedPnlUsd == null ? '' : fmtUsd(t.realizedPnlUsd),
      t.status,
    ]),
    [108, 40, 50, 54, 54, 58, 68, 58, 50],
    ['l', 'l', 'l', 'l', 'r', 'r', 'r', 'r', 'l']
  );

  pages.forEach((ops, i) => {
    ops.push(`${muted} rg`);
    ops.push(
      `BT /F1 7 Tf ${M.toFixed(2)} 22 Td (${pdfEscape(`Page ${i + 1} of ${pages.length}   |   Omni Trading   |   Confidential`)}) Tj ET`
    );
  });

  const objects = [];
  const add = (body) => {
    objects.push(body);
    return objects.length;
  };

  add('<< /Type /Catalog /Pages 2 0 R >>');
  add('<< /Type /Pages /Kids [] /Count 0 >>');
  const font1 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const font2 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
  const contentObjIds = pages.map((ops) => {
    const body = `${ops.join('\n')}\n`;
    return add(`<< /Length ${body.length} >>\nstream\n${body}endstream`);
  });
  const pageObjIds = pages.map((_, i) =>
    add(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 ${font1} 0 R /F2 ${font2} 0 R >> >> /Contents ${contentObjIds[i]} 0 R >>`
    )
  );
  objects[1] = `<< /Type /Pages /Kids [${pageObjIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageObjIds.length} >>`;

  let out = '%PDF-1.4\n';
  const xref = [0];
  objects.forEach((obj, i) => {
    xref.push(out.length);
    out += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xrefStart = out.length;
  out += `xref\n0 ${objects.length + 1}\n`;
  out += '0000000000 65535 f \n';
  xref.slice(1).forEach((off) => {
    out += `${String(off).padStart(10, '0')} 00000 n \n`;
  });
  out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  return out;
}

export function downloadDiscScalpingCsv(report) {
  const csv = buildDiscScalpingCsv(report);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${fileBase(report)}.csv`);
}

export function downloadDiscScalpingPdf(report) {
  const text = buildPdfBytes(report);
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) bytes[i] = text.charCodeAt(i) & 0xff;
  downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${fileBase(report)}.pdf`);
}
